// Phase 1020 — Reset Password — redeem the one-time token from the email link.
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Key } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [form, setForm] = useState({ new_password: '', confirm_password: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Missing reset token. Please open the link from your email.');
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, ...form });
      toast.success('Password reset. Please log in with your new password.');
      setTimeout(() => navigate('/login'), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-zinc-200 p-8">
        <div className="flex items-center gap-3 mb-6">
          <Key size={24} className="text-zinc-900" />
          <h1 className="text-xl font-semibold text-zinc-900">Reset Password</h1>
        </div>
        {!token ? (
          <div className="space-y-4">
            <p className="text-sm text-red-600">Missing reset token. Please open the link from your email.</p>
            <Link to="/forgot-password" className="inline-block px-4 h-9 leading-9 text-xs font-semibold uppercase tracking-wider bg-zinc-900 text-white hover:bg-zinc-700" data-testid="reset-request-new">Request a new link</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>New Password *</Label>
              <Input type="password" required autoComplete="new-password"
                     value={form.new_password}
                     onChange={e => setForm({ ...form, new_password: e.target.value })}
                     className="rounded-none mt-1" data-testid="reset-new-pw" />
              <p className="text-[11px] text-zinc-400 mt-1">At least 8 characters, must contain letters and digits.</p>
            </div>
            <div>
              <Label>Confirm Password *</Label>
              <Input type="password" required autoComplete="new-password"
                     value={form.confirm_password}
                     onChange={e => setForm({ ...form, confirm_password: e.target.value })}
                     className="rounded-none mt-1" data-testid="reset-confirm-pw" />
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-none bg-zinc-950 hover:bg-zinc-800" data-testid="reset-submit">
              {busy ? 'Resetting…' : 'Reset password'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
